import Vue from 'vue'

const errorText = (error) => `${error.status} - ${error.body.reason}`

export default {
  /* #####################################################################
  *
  *      Authentication
  *
  * #####################################################################
  */
  apiAuthLogin (payload) {
    return Vue.http.post(`api/oauth/login`, payload).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },

  apiAuthForgotPassword (payload) {
    return Vue.http.post(`api/oauth/forgot-password`, payload).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },

  apiAuthResetPassword (payload) {
    const {token, ...rest} = payload
    return Vue.http.post(`api/oauth/password-reset?request=${token}`, rest).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },

  /* #####################################################################
  *
  *      Invoices
  *
  * #####################################################################
  */
  getAllInvoices (getAll) {
    return Vue.http.get(`api_v2/invoice?getAll=${getAll}`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  getAllClientInvoices (id) {
    return Vue.http.get(`api_v2/client/${id}/invoices`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  getSingleInvoice (id) {
    return Vue.http.get(`api_v2/invoice/${id}`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  addInvoiceAdjustment (data) {
    return Vue.http.post(`api_v2/adjustment`, data).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  updateInvoiceAdjustment (data) {
    return Vue.http.put(`api_v2/adjustment/${data.id}`, data).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  deleteInvoiceAdjustment (id) {
    return Vue.http.delete(`api_v2/adjustment/${id}`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  updateSingleInvoice (update) {
    return Vue.http.put(`api_v2/invoice/${update.invoiceId}`, update).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  uploadSingleInvoiceFile (data) {
    return Vue.http.post(`api_v2/invoice/${data.id}/upload`, data.file).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  /* #####################################################################
  *
  *      Users
  *
  * #####################################################################
  */
  getAllUsers () {
    return Vue.http.get('api_v2/user').then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  getSingleUser (id) {
    return Vue.http.get(`api_v2/user/${id}`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  updateSingleUser (data) {
    return Vue.http.put(`api_v2/user/${data.hash_id}`, data).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  deleteSingleUser (id) {
    return Vue.http.delete(`api_v2/user/${id}`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  resetUserPassword (id) {
    return Vue.http.get(`api_v2/user/${id}/reset-password`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  addSingleUser (data) {
    return Vue.http.post(`api_v2/user`, data).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  uploadUserLogo (data) {
    return Vue.http.post(`api_v2/user/${data.id}/logo`, data.file).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  deleteUserLogo (id) {
    return Vue.http.delete(`api_v2/user/${id}/logo`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  /* #####################################################################
  *
  *      Clients
  *
  * #####################################################################
  */
  getAllClients (includeAssociateUsers) {
    const url = includeAssociateUsers ? `api_v2/client?includeAssociateUsers=${includeAssociateUsers}` : 'api_v2/client'
    return Vue.http.get(url).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  getUsageStats (id, to, from) {
    return Vue.http.get(`api_v2/client/${id}/stats?to=${to}&from=${from}`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  getSingleClient (id, params) {
    return Vue.http.get(`api_v2/client/${id}`, params).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  updateSingleClient (data) {
    return Vue.http.put(`api_v2/client/${data.hash_id}`, data).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  addSingleClient (data) {
    return Vue.http.post(`api_v2/client`, data).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  deleteSingleClient (id) {
    return Vue.http.delete(`api_v2/client/${id}`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  uploadSingleImage (data) {
    return Vue.http.post(`api_v2/client/${data.id}/logo`, data.file).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  deleteSingleImage (id) {
    return Vue.http.delete(`api_v2/client/${id}/logo`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  /* #####################################################################
  *
  *      Encoding Profile
  *
  * #####################################################################
  */
  getAllEncodingProfiles () {
    return Vue.http.get(`api_v2/encoding_profiles`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  assignEncodingProfile (payload) {
    return Vue.http.post('api_v2/encoding_profile/assign', payload).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  deleteSingleEncodingProfile (id) {
    return Vue.http.delete(`api_v2/encoding_profile/${id}`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  addSingleEncodingProfile (payload) {
    return Vue.http.post(`api_v2/encoding_profile`, payload).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  updateSingleEncodingProfile (payload) {
    return Vue.http.put(`api_v2/encoding_profile`, payload).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  cloneEncodingProfile (payload) {
    return Vue.http.post(`api_v2/encoding_profile/clone`, payload).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  /* #####################################################################
  *
  *      Plugins
  *
  * #####################################################################
  */
  getAllPlugins () {
    return Vue.http.get('api_v2/plugins').then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  addSinglePlugin (payload) {
    return Vue.http.post(`api_v2/plugin`, payload).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  /* #####################################################################
  *
  *      Fonts
  *
  * #####################################################################
  */
  getAllFonts () {
    return Vue.http.get('api_v2/fonts').then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  addSingleFont (payload) {
    return Vue.http.post(`api_v2/font`, payload).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  /* #####################################################################
  *
  *      Profile
  *
  * #####################################################################
  */
  getCurrentUser () {
    return Vue.http.get(`api_v2/user/current`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  /* #####################################################################
  *
  *      Campaigns
  *
  * #####################################################################
  */
  getAllCampaigns () {
    return Vue.http.get('api_v2/campaign').then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  getClientCampaigns (id) {
    return Vue.http.get(`api_v2/client/${id}/campaigns`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  addSingleCampaign (campaign) {
    return Vue.http.post('api_v2/campaign', campaign).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  getSingleCampaign (id) {
    return Vue.http.get(`api_v2/campaign/${id}`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  updateSingleCampaign (data) {
    return Vue.http.put(`api_v2/campaign/${data.id}`, data).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  deleteSingleCampaign (id) {
    return Vue.http.delete(`api_v2/campaign/${id}`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  cloneSingleCampaign (payload) {
    return Vue.http.post(`api_v2/campaign/${payload.campaignId}/clone`, payload).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  cloneSingleCampaignStatus (hashid) {
    return Vue.http.get(`api_v2/cloneentry/${hashid}`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  /* #####################################################################
  *
  *      Creatives
  *
  * #####################################################################
  */
  addSingleCreative (creative) {
    return Vue.http.post('api_v2/creative', creative).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  setCreativeStatusToEdited (data) {
    return Vue.http.post(`api_v2/creative_unit/update_to_edited/`, data).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  getAllCreatives (id) {
    return Vue.http.get(`api_v2/campaign/${id}/creatives`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  deleteSingleCreative (id) {
    return Vue.http.delete(`api_v2/creative/${id}`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  updateSingleCreative (data) {
    return Vue.http.put(`api_v2/creative/${data.hash_id}`, data).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  editSingleCreativeName (data) {
    return Vue.http.put(`api_v2/creative/${data.hash_id}`, data).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  getAllCreativeUnits (id, {pager, sorting, searchTerms}) {
    const sortingQuery = sorting && sorting.length > 0 ? `&sort=${JSON.stringify(sorting)}` : ''
    const searchTermsQuery = searchTerms ? `&search=${searchTerms}` : ''
    const queryString = `?page=${pager.page}&pageSize=${pager.pageSize}${sortingQuery}${searchTermsQuery}`
    return Vue.http.get(`api_v2/creative/${id}/units${queryString}`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  associateSingleCreative (data) {
    return Vue.http.post(`api_v2/creative/associations`, data).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  creativePushAssociations (data) {
    return Vue.http.post(`api_v2/creative/associations/push`, data).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  creativePushBulkAssociations (data) {
    return Vue.http.post(`api_v2/creative/associations/push/bulk`, data).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  editAssociateSingleCreative (data) {
    return Vue.http.patch(`api_v2/creative/associations`, data).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  requestCreativeDownloadApproval (data) {
    return Vue.http.post(`api_v2/creative/request_approval`, data).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  approveCreativeDownloadApproval (data) {
    return Vue.http.post(`api_v2/creative/approve`, data).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  updateAeFile (data) {
    return Vue.http.post(`api_v2/creative/update/aefile`, data).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  getCreativeUnit (id) {
    return Vue.http.get(`api_v2/creative/singleunit/${id}`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  updateSingleCreativeUnit (data) {
    return Vue.http.put(`api_v2/creative/singleunit/${data.id}`, [data.columns, ...data.unit]).then(response => {
      console.log(response.data)
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  updateAllCreativeUnits (data, {pager, sorting, searchTerms}) {
    const sortingQuery = sorting && sorting.length > 0 ? `&sort=${JSON.stringify(sorting)}` : ''
    const searchTermsQuery = searchTerms ? `&search=${searchTerms}` : ''
    const queryString = `?page=${pager.page}&pageSize=${pager.pageSize}${sortingQuery}${searchTermsQuery}`
    return Vue.http.put(`api_v2/creative/${data.id}/units${queryString}`, [data.flag, data.columns, ...data.unit]).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  updateBulkCreativeUnits (data) {
    return Vue.http.post(`api_v2/creative/${data.id}/bulk`, data).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  uploadCsvToCreative (data) {
    return Vue.http.post(`api_v2/creative/${data.id}/upload`, data.file).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  cloneSingleCreative (data) {
    return Vue.http.post(`api_v2/creative/${data.creativeId}/clone`, {cloneCreativeName: data.cloneCreativeName}).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  reviewLInkHistory (creativeHashId) {
    return Vue.http.get(`api_v2/review/${creativeHashId}/history`).then(
      response => response.data,
      error => {
        throw errorText(error)
      })
  },
  cancelRendersRequests (hashId) {
    return Vue.http.put(`api_v2/creative/${hashId}/cancel_renders`).then(
      response => response.data,
      error => {
        throw errorText(error)
      }
    )
  },
  /* #####################################################################
  *
  *      Assets
  *
  * #####################################################################
  */
  getAllAssets (id) {
    return Vue.http.get(`api_v2/creative/${id}/assets`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  getAllImageAssets (id) {
    return Vue.http.get(`api_v2/creative/${id}/imageassets`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  getAllVideoAssets (id) {
    return Vue.http.get(`api_v2/creative/${id}/videoassets`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  uploadAssetsToCreative (data) {
    return Vue.http.post(`api_v2/creative/${data.id}/assets`, data.file).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  copyAssetsToCreative (data) {
    return Vue.http.post(`api_v2/creative/${data.id}/copy-assets`, data).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  deleteSingleAsset (data) {
    return Vue.http.delete(`api_v2/creative/${data.id}/assets/${data.fileName}`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  uploadFootage (data, commit, state) {
    // console.log('client side upload started at: ' + new Date().toLocaleTimeString())
    return Vue.http.post(`api_v2/creative/${data.id}/footage`, data.file, {
      progress (e) {
        if (e.loaded === e.total) {
          state.footageLoadingVal = null
        } else {
          state.footageLoadingVal = Math.round(e.loaded / e.total * 100)
        }
      }
    }).then(response => {
      // console.log('response from server at: ' + new Date().toLocaleTimeString())
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  deleteFootage (data, commit, state) {
    // console.log('client side upload started at: ' + new Date().toLocaleTimeString())
    return Vue.http.delete(`api_v2/creative/${data.id}/footage`, data.file).then(response => {
      // console.log('response from server at: ' + new Date().toLocaleTimeString())
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  /* #####################################################################
  *
  *      Links
  *
  * #####################################################################
  */
  getAllLinks ({type, page, pageSize, searchTerms}) {
    const queryString = `?type=${type}&page=${page}&pageSize=${pageSize}`
    const searchTermsQuery = searchTerms ? `&search=${searchTerms}` : ''

    return Vue.http.get(`api_v2/review${queryString}${searchTermsQuery}`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  deleteSingleLink (id) {
    return Vue.http.delete(`api_v2/review/${id}`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  refreshSingleLink (id) {
    return Vue.http.get(`api_v2/review/${id}/refresh`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  updateSingleLink (data) {
    return Vue.http.put(`api_v2/review/${data.id}`, data).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  validateClone (data) {
    return Vue.http.get(`api_v2/review/${data.id}/validate-clone`).then(({data}) => data, error => { throw errorText(error) })
  },
  /* #####################################################################
  *
  *      Tags
  *
  * #####################################################################
  */
  getAllTags () {
    return Vue.http.get(`api_v2/adtag`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  getAllTagsCreative (id) {
    return Vue.http.get(`api_v2/adtag/creative/${id}`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  getSingleTag (id) {
    return Vue.http.get(`api_v2/adtag/${id}`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  deleteSingleTag (id) {
    return Vue.http.delete(`api_v2/adtag/${id}`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  refreshSingleTag (id) {
    return Vue.http.get(`api_v2/adtag/${id}/refresh`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  updateSingleTag (data) {
    return Vue.http.put(`api_v2/adtag/${data.id}`, data).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  /* #####################################################################
  *
  *      Downloads
  *
  * #####################################################################
  */
  getUserDownloads () {
    return Vue.http.get(`api_v2/downloads/user/list`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  getCreativeDownloads (id) {
    return Vue.http.get(`api_v2/downloads/creative/${id}`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  getDownload (id) {
    return Vue.http.get(`api_v2/downloads/${id}`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  createSignedDownloadRequest (payload) {
    return Vue.http.post('api_v2/download/request', payload).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  processDownload (payload) {
    return Vue.http.post(`api_v2/downloads/process`, payload).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  /* #####################################################################
  *
  *      Monitor
  *
  * #####################################################################
  */
  getRenderHealth () {
    return Vue.http.get('api_v2/health/render').then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  getRenderBots () {
    return Vue.http.get('api_v2/render_bots').then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  getRenderBatches () {
    return Vue.http.get('api_v2/render-batch').then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  deleteRenderBatch (id) {
    return Vue.http.delete(`api_v2/render-batch/${id}`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  updateRenderQueue (payload) {
    return Vue.http.put(`api_v2/queue/${payload.id}`, payload).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  updateRenderQueueBatchWise (payload) {
    return Vue.http.put(`api_v2/batch/${payload.id}`, payload).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  deleteRenderQueue (id) {
    return Vue.http.delete(`api_v2/queue/${id}`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  },
  deleteRenderBot (id) {
    return Vue.http.delete(`api_v2/render_bot/${id}`).then(response => {
      return response.data
    }, error => {
      throw errorText(error)
    })
  }
}



// WEBPACK FOOTER //
// ./src/store/api.js